import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ApiService from '../services/ApiService';
import { useOrder } from '../order/OrderContext';
import { useAuth } from '../account/AuthContext';
import FileSizeGuestModal from './FileSizeGuestModal';
import FileSizeLimitModal from './FileSizeLimitModal';

// Komponent dropzone z pełną integracją backend API
const Dropzone = ({ onFilesUploaded }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const { sessionId, createNewSession } = useOrder();
    const { isAuthenticated } = useAuth();

    // Modale dla limitów plików
    const [showGuestModal, setShowGuestModal] = useState(false);
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [oversizedFile, setOversizedFile] = useState(null);

    // Limity w bajtach
    const GUEST_LIMIT = 25 * 1024 * 1024; // 25 MB
    const USER_LIMIT = 100 * 1024 * 1024; // 100 MB

    const onDrop = useCallback(async (acceptedFiles) => {
        // Filtruj pliki STL, STP, STEP
        const modelFiles = acceptedFiles.filter(file => {
            const name = file.name.toLowerCase();
            return name.endsWith('.stl') || name.endsWith('.stp') || name.endsWith('.step');
        });

        if (modelFiles.length === 0) {
            alert('Proszę wybrać pliki w formacie STL, STP lub STEP');
            return;
        }

        // Walidacja rozmiaru plików PRZED wysyłką
        const maxSize = isAuthenticated ? USER_LIMIT : GUEST_LIMIT;
        const oversized = modelFiles.find(file => file.size > maxSize);

        if (oversized) {
            setOversizedFile(oversized);
            if (isAuthenticated) {
                // Zalogowany użytkownik przekroczył 100 MB
                setShowLimitModal(true);
            } else {
                // Gość przekroczył 25 MB
                setShowGuestModal(true);
            }
            return; // Zatrzymaj upload
        }

        setIsUploading(true);

        // Sprawdź czy mamy sessionId, jeśli nie - utwórz nową sesję
        let currentSessionId = sessionId;
        if (!currentSessionId) {
            try {
                currentSessionId = await createNewSession();
            } catch (error) {
                // Fallback - użyj starego sposobu generowania sessionId
                currentSessionId = `session_${Date.now()}`;
            }
        }

        const processedModels = [];

        try {
            // Przetwarzaj każdy plik sekwencyjnie
            for (const file of modelFiles) {
                const fileId = Date.now() + Math.random();
                setUploadProgress(prev => ({
                    ...prev,
                    [fileId]: { stage: 'uploading', progress: 20 }
                }));

                try {
                    // Krok 1: Uploadujemy plik do backendu
                    const { key } = await ApiService.uploadModelFile(file, currentSessionId);

                    setUploadProgress(prev => ({
                        ...prev,
                        [fileId]: { stage: 'analyzing', progress: 50 }
                    }));

                    // Krok 2: Analizuj model
                    const analysis = await ApiService.analyzeModel(key, currentSessionId);

                    setUploadProgress(prev => ({
                        ...prev,
                        [fileId]: { stage: 'calculating', progress: 90 }
                    }));

                    // Krok 3: Przygotuj dane modelu
                    const modelData = {
                        id: fileId,
                        file,
                        name: file.name,
                        size: file.size,
                        key,
                        sessionId: currentSessionId, // DODANE: zapisz sessionId w modelu
                        volume: analysis.volume,
                        dimensions: analysis.dimensions,
                        boundingBox: analysis.boundingBox,
                        thumbnailUrl: analysis.thumbnailUrl,
                        //walidacja max wymiarów
                        hasOversizedDimension: analysis.hasOversizedDimension,
                        oversized: analysis.oversized,
                        maxDimension: analysis.maxDimension,

                        // Domyślne parametry druku
                        technology: 'FDM',
                        material: 'PLA',
                        color: 'white',
                        infill: 20,
                        quantity: 1,
                        price: 0
                    };

                    processedModels.push(modelData);

                    setUploadProgress(prev => ({
                        ...prev,
                        [fileId]: { stage: 'complete', progress: 100 }
                    }));

                } catch (error) {
                    setUploadProgress(prev => ({
                        ...prev,
                        [fileId]: { stage: 'error', progress: 0, error: error.message }
                    }));

                    // Dodaj model z błędem
                    processedModels.push({
                        id: fileId,
                        file,
                        name: file.name,
                        size: file.size,
                        volume: 0,
                        dimensions: { x: 0, y: 0, z: 0 },
                        previewPng: null,
                        technology: 'FDM',
                        material: 'PLA',
                        color: 'white',
                        infill: 20,
                        quantity: 1,
                        price: 0,
                        error: error.message,
                        errorType: error.errorType || 'upload_failed',
                        errorMessage: error.message,
                        errorDetails: error.errorDetails || null
                    });
                }
            }

            // Przekaż wszystkie przetworzone modele
            onFilesUploaded(processedModels);

        } finally {
            setIsUploading(false);
            // Wyczyść progress po 2 sekundach
            setTimeout(() => setUploadProgress({}), 2000);
        }
    }, [onFilesUploaded, sessionId, createNewSession, isAuthenticated]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/octet-stream': ['.stl', '.stp', '.step'],
            'model/stl': ['.stl'],
            'application/step': ['.stp', '.step']
        },
        multiple: true,
        disabled: isUploading
    });

    // Dynamiczny limit w zależności od statusu zalogowania
    const maxSizeDisplay = isAuthenticated
        ? 'max 100 MB'
        : 'max 25 MB (100 MB dla zalogowanych)';

    // SVG ikona dla przycisku
    const UploadIcon = () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 18V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 15L12 12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );

    return (
        <>
        <div
        {...getRootProps()}
        className={`dropzone-container ${isDragActive ? 'dropzone-drag-active' : ''} ${isUploading ? 'dropzone-uploading' : ''}`}
        >
        <input {...getInputProps()} />

        {isUploading ? (
            <div>
            <div className="dropzone-icon">⏳</div>
            <p className="dropzone-main-text">
            Przetwarzanie plików...
            </p>

            {/* Progress dla każdego pliku */}
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
                <div key={fileId} className="upload-progress-item">
                <div className="upload-progress-text">
                {progress.stage === 'requesting' && 'Przygotowywanie uploadu...'}
                {progress.stage === 'uploading' && 'Wysyłanie pliku...'}
                {progress.stage === 'analyzing' && 'Analizowanie modelu...'}
                {progress.stage === 'calculating' && 'Obliczanie...'}
                {progress.stage === 'complete' && '✅ Gotowe'}
                {progress.stage === 'error' && `❌ Błąd: ${progress.error}`}
                </div>
                <div className="upload-progress-bar">
                <div
                className="upload-progress-fill"
                style={{
                    width: `${progress.progress}%`,
                    backgroundColor: progress.stage === 'error' ? '#e74c3c' : '#F53447'
                }}
                />
                </div>
                </div>
            ))}
            </div>
        ) : (
            <div>
            <div className="dropzone-icon">📁</div>
            <p className="dropzone-main-text">
            Upuść plik 3D by sprawdzić cenę
            </p>

            {/* Formaty plików */}
            <div className="dropzone-formats">
            <div className="format-chips">
            <span className="format-chip">.STL</span>
            <span className="format-chip">.STP</span>
            <span className="format-chip">.STEP</span>
            </div>
            <span className="max-size-info">{maxSizeDisplay}</span>
            </div>

            {/* Informacja o maksymalnych wymiarach */}
            <div className="dropzone-dimensions-info">
            <span className="dimensions-label">Maksymalny rozmiar części:</span>
            <span className="dimensions-value">250 × 250 × 250 mm</span>
            </div>

            {/* Przycisk upload */}
            <button className="dropzone-upload-btn" type="button">
            <UploadIcon />
            Prześlij z dysku
            </button>

            {/* Informacja o prywatności */}
            <p className="dropzone-security-info">
            Pliki 3D służą wyłącznie do przygotowania wyceny lub realizacji zamówienia.
            <br />
            Przesłanie pliku nie oznacza żadnych zobowiązań. Pełna kontrola po Twojej stronie.
            </p>
            </div>
        )}
        </div>

        {/* Modale dla limitów plików */}
        <FileSizeGuestModal
        isOpen={showGuestModal}
        onClose={() => {
            setShowGuestModal(false);
            setOversizedFile(null);
        }}
        fileSize={oversizedFile?.size || 0}
        />

        <FileSizeLimitModal
        isOpen={showLimitModal}
        onClose={() => {
            setShowLimitModal(false);
            setOversizedFile(null);
        }}
        fileSize={oversizedFile?.size || 0}
        />
        </>
    );
};

export default Dropzone;
